import { useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const lc = (v) => String(v ?? "").toLowerCase();

/** Category filter values: material category plus family (Paper, Vinyl, SAV Sticker...), so both kinds of grouping work. */
export const categoryOptions = (mats) => [...new Set(mats.flatMap((m) => [m.category, m.family]).filter(Boolean))].sort((a, b) => a.localeCompare(b));

export const matchMaterial = (f) => (m) => {
  if (!m.code && !m.name) return true;
  if (f.category && m.category !== f.category && m.family !== f.category) return false;
  if (f.form && (m.form || "sheet") !== f.form) return false;
  const q = lc(f.q).trim();
  return !q || lc(m.name).includes(q) || lc(m.code).includes(q);
};

export function MaterialFilterBar({ f, setF, mats, shown }) {
  return (
    <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 p-4" data-testid="material-filters">
      <div className="relative min-w-[220px] flex-1"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
        <Input className="pl-8" placeholder="Search name or code" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="material-filter-search" /></div>
      <div className="w-56"><NativeSelect testId="material-filter-category" value={f.category} onChange={(v) => setF({ ...f, category: v })} placeholder="All categories" options={categoryOptions(mats).map((c) => ({ value: c, label: c }))} /></div>
      <div className="w-40"><NativeSelect testId="material-filter-form" value={f.form} onChange={(v) => setF({ ...f, form: v })} placeholder="Sheet and roll" options={[{ value: "sheet", label: "Sheet" }, { value: "roll", label: "Roll" }]} /></div>
      <span className="pb-2 text-xs text-slate-500" data-testid="material-filter-count">{shown} of {mats.length}</span>
      {(f.q || f.category || f.form) && <button type="button" className="pb-2 text-xs text-[#389F95] underline" onClick={() => setF({ q: "", category: "", form: "" })} data-testid="material-filter-clear">Clear</button>}
    </div>
  );
}

/** Delete with guard: the server refuses materials used on quotes, jobs or stock movements; unused ones are confirmed, then removed with their stock item. */
export function useMaterialDelete(savedCodes, onDone) {
  const [pending, setPending] = useState(null);
  const ask = (row) => {
    if (!row.code || !savedCodes.has(row.code)) return Promise.resolve(true);
    return new Promise((resolve) => setPending({ row, resolve }));
  };
  const close = (ok) => { pending?.resolve(ok); setPending(null); };
  const confirm = async () => {
    try { const r = (await api.delete(`/estimating/materials/${encodeURIComponent(pending.row.code)}`)).data; toast.success(`Deleted ${pending.row.code}${r.inventory_removed ? " and its stock item" : ""}`); close(true); onDone(); }
    catch (e) { toast.error(errMsg(e), { duration: 8000 }); close(false); }
  };
  const dialog = (
    <AlertDialog open={!!pending} onOpenChange={(o) => !o && close(false)}>
      <AlertDialogContent data-testid="material-delete-dialog">
        <AlertDialogHeader><AlertDialogTitle>Delete {pending?.row.name || pending?.row.code}?</AlertDialogTitle>
          <AlertDialogDescription>This removes {pending?.row.code} from the material list and its unused stock item from inventory. It can't be undone. Materials used on quotes, jobs or stock movements can't be deleted.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel data-testid="material-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={(e) => { e.preventDefault(); confirm(); }} data-testid="material-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return [ask, dialog];
}
